import type { SkillDef } from "../types";
import { digits, fmt, nz, placeParts, prob } from "../util";

function hint(a: number, b: number): string {
  if (a === 11 || b === 11) {
    const n = a === 11 ? b : a;
    return `×11 (Trachtenberg): each digit + its right-hand neighbor, right to left, carrying.\nCheck: ${fmt(n)}×10 + ${fmt(n)} = ${fmt(n * 10)} + ${fmt(n)} = ${fmt(n * 11)}`;
  }
  if (a >= 11 && a <= 19 && b >= 11 && b <= 19)
    return `Teens: (${a} + ${b % 10})×10 + ${a % 10}×${b % 10} = ${(a + (b % 10)) * 10} + ${(a % 10) * (b % 10)} = ${a * b}`;
  const s = Math.min(a, b), g = Math.max(a, b), parts = placeParts(s);
  const lines = parts.map((p) => `${fmt(g)} × ${fmt(p)} = ${fmt(g * p)}`);
  if (parts.length > 1) lines.push(`Sum: ${parts.map((p) => fmt(g * p)).join(" + ")} = ${fmt(a * b)}`);
  return "Split the smaller factor:\n" + lines.join("\n");
}

export const mul: SkillDef = {
  key: "mul", name: "Multiplication", blurb: "×11, teens, splitting", start: 1,
  generate(L, r) {
    let a: number, b: number;
    const R = r.float();
    switch (L) {
      case 1: a = r.int(2, 5); b = r.int(2, 9); break;
      case 2: a = r.int(3, 12); b = r.int(3, 12); break;
      case 3: a = r.int(12, 29); b = r.int(2, 5); break;
      case 4: a = r.int(12, 99); b = r.int(3, 9); break;
      case 5: if (R < 0.5) { a = r.int(11, 19); b = r.int(11, 19); } else { a = r.int(12, 99); b = 11; } break;
      case 6: a = r.int(21, 99); b = r.int(11, 30); break;
      case 7: a = r.int(31, 99); b = r.int(31, 99); break;
      case 8: a = r.int(101, 999); b = R < 0.3 ? 11 : r.int(12, 25); break;
      case 9: a = r.int(101, 999); b = r.int(26, 99); break;
      default: a = r.int(101, 999); b = r.int(101, 999);
    }
    let t: number;
    if (a <= 12 && b <= 12) t = 3;
    else if (a === 11 || b === 11) t = 2 + 1.2 * digits(a === 11 ? b : a);
    else if (a >= 11 && a <= 19 && b >= 11 && b <= 19) t = 6;
    else t = 1.5 + 2.8 * nz(a) * nz(b) + 0.6 * digits(a * b);
    return prob(`${fmt(a)} × ${fmt(b)}`, a * b, hint(a, b), { t });
  },
};
